/**
 * The things a level implies but does not author: how big it is, how tall the
 * room is, and where the lights hang.
 *
 * All three are derived from `world.solids` rather than added to the map
 * format, and that is the same argument `materials.ts` makes about themes. A
 * `MapSolid` is six numbers and `mapHash` covers every one of them, so anything
 * added here would have to be agreed on in the join handshake — and a ceiling
 * fitting is not something a client and server can usefully disagree about.
 * Nothing in this file is collidable, reaches the wire, or is read by the
 * simulation.
 *
 * Kept out of `scene.ts` and free of Three.js because these are pure functions
 * over plain boxes: `fixturePositions` in particular is a placement rule, and a
 * placement rule that drops a light inside a wall is much easier to reason
 * about as an array of coordinates than as something you notice in a
 * screenshot.
 */

import type { StaticWorld } from "@prop-hunt/shared";

/** The XZ extent of a level, in world metres. */
export interface MapBounds {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
}

/**
 * Storey height used when a world has no solids to measure.
 *
 * Only reachable from the empty-world case, which the game itself never builds
 * — every real map opens with a ground slab. It exists so callers can multiply
 * by this without a zero propagating into a shadow camera's far plane.
 */
const FALLBACK_STOREY = 3;

/**
 * How far below the tallest solid a box can top out and still count as a wall.
 *
 * Walls in both shipped maps are a single authored height (4m on depot, 3m on
 * office) so this only has to absorb float noise, not genuine variation.
 */
const WALL_TOP_TOLERANCE = 0.01;

/**
 * The level's footprint, taken from the union of every collision solid.
 *
 * That is the ground slab in practice — it is the largest box in both maps —
 * but taking the union rather than looking for a slab means a map whose floor
 * is authored as several tiles still measures correctly.
 */
export function mapBounds(world: StaticWorld): MapBounds {
  if (world.solids.length === 0) return { minX: 0, maxX: 0, minZ: 0, maxZ: 0 };

  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (const solid of world.solids) {
    if (solid.minX < minX) minX = solid.minX;
    if (solid.maxX > maxX) maxX = solid.maxX;
    if (solid.minZ < minZ) minZ = solid.minZ;
    if (solid.maxZ > maxZ) maxZ = solid.maxZ;
  }
  return { minX, maxX, minZ, maxZ };
}

/**
 * How high the ceiling sits: the top of the tallest thing in the level.
 *
 * Walls are what that measures, and deliberately. Putting the lid at wall
 * height means no wall pokes through it and no gap opens between the two, on
 * any map, without the map having to say how tall it is.
 */
export function ceilingHeight(world: StaticWorld): number {
  let top = 0;
  for (const solid of world.solids) {
    if (solid.maxY > top) top = solid.maxY;
  }
  return top > 0 ? top : FALLBACK_STOREY;
}

/**
 * Where to hang ceiling fittings: a grid at `spacing` metres, centred on the
 * level, with any fitting that would intersect a wall dropped.
 *
 * Centred rather than started from a corner so the row nearest each wall is
 * inset by the same amount at both ends — an off-centre grid reads as a
 * mistake even to someone who could not say why.
 *
 * The rejection test is what keeps this honest. A fitting is a visible box
 * hanging just under the lid, so one placed inside a wall is half-buried
 * geometry, and a `PointLight` at the same spot lights the wall's interior and
 * nothing else. Only solids that reach the ceiling reject a fitting: waist-high
 * desk runs pass under it, which is the entire point of hanging it up there.
 *
 * `half` is the fitting's half-extent, so the test is against the box that will
 * actually be drawn rather than against its centre point.
 */
export function fixturePositions(
  world: StaticWorld,
  spacing: number,
  half: number,
): { x: number; z: number }[] {
  const bounds = mapBounds(world);
  const storey = ceilingHeight(world);
  const width = bounds.maxX - bounds.minX;
  const depth = bounds.maxZ - bounds.minZ;
  if (width <= 0 || depth <= 0 || spacing <= 0) return [];

  const cols = Math.max(1, Math.floor(width / spacing));
  const rows = Math.max(1, Math.floor(depth / spacing));
  const originX = (bounds.minX + bounds.maxX) / 2 - ((cols - 1) * spacing) / 2;
  const originZ = (bounds.minZ + bounds.maxZ) / 2 - ((rows - 1) * spacing) / 2;

  // Reused across every probe: `query` writes into it rather than allocating,
  // and this runs cols*rows times at scene build.
  const scratch: number[] = [];
  const out: { x: number; z: number }[] = [];

  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const x = originX + col * spacing;
      const z = originZ + row * spacing;
      if (!meetsCeiling(world, scratch, x, z, half, storey)) out.push({ x, z });
    }
  }
  return out;
}

/**
 * Whether a fitting centred at (x, z) would run into something that reaches the
 * ceiling.
 *
 * `query` is a broadphase over grid cells and returns candidates, not hits, so
 * the precise XZ overlap has to be redone here. Strict inequalities: a fitting
 * exactly flush against a wall's face touches it without intersecting it, and
 * rejecting those would empty every row along the perimeter.
 */
function meetsCeiling(
  world: StaticWorld,
  scratch: number[],
  x: number,
  z: number,
  half: number,
  storey: number,
): boolean {
  const minX = x - half;
  const maxX = x + half;
  const minZ = z - half;
  const maxZ = z + half;

  for (const index of world.query(minX, minZ, maxX, maxZ, scratch)) {
    const solid = world.solids[index]!;
    if (solid.maxY < storey - WALL_TOP_TOLERANCE) continue;
    if (solid.minX < maxX && solid.maxX > minX && solid.minZ < maxZ && solid.maxZ > minZ) {
      return true;
    }
  }
  return false;
}
